import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { fetchMutations, MutationResponse, MutationStatus } from '../../services/mutationService';
import { fetchBranchesAsync } from '../branch/branchSlice';
import CreateMutationModal from './CreateMutationModal';

const CompleteMutationPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { branches } = useSelector((state: RootState) => state.branch);
  const [mutations, setMutations] = useState<MutationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState<MutationStatus | 'ALL'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  
  useEffect(() => {
    // Load branches if not already loaded
    if (branches.length === 0) {
      dispatch(fetchBranchesAsync({}));
    }
    loadMutations();
  }, [statusFilter, dispatch, branches.length]);
  
  const loadMutations = async () => {
    setLoading(true);
    try {
      const result = await fetchMutations({
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        page: 1,
        page_size: 50
      });
      setMutations(result);
    } catch (error) {
      console.error('Failed to load mutations:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusBadge = (status: MutationStatus) => {
    const badges = {
      [MutationStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
      [MutationStatus.CONFIRMED]: 'bg-green-100 text-green-800',
      [MutationStatus.CANCELLED]: 'bg-red-100 text-red-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  // Helper function to get branch name
  const getBranchName = (branchId: number) => {
    const branch = branches.find(b => b.id === branchId);
    return branch ? `${branch.code} - ${branch.name}` : `Cabang #${branchId}`;
  };

  const filteredMutations = mutations.filter(mutation => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      mutation.mutation_no.toLowerCase().includes(query) ||
      mutation.items.some(item => 
        item.item_name.toLowerCase().includes(query) ||
        item.item_sku.toLowerCase().includes(query)
      )
    );
  });

  const printDeliveryNote = (mutation: MutationResponse) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Surat Jalan - ${mutation.mutation_no}</title>
        <style>
          @media print {
            @page { margin: 1cm; }
            body { margin: 0; }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
          }
          .header h1 {
            margin: 0;
            font-size: 24px;
          }
          .header h2 {
            margin: 5px 0;
            font-size: 18px;
            font-weight: normal;
          }
          .info-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 30px;
          }
          .info-box {
            border: 1px solid #ddd;
            padding: 15px;
            border-radius: 5px;
          }
          .info-box h3 {
            margin: 0 0 10px 0;
            font-size: 14px;
            color: #666;
          }
          .info-box p {
            margin: 5px 0;
            font-size: 14px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 10px;
            text-align: left;
          }
          th {
            background-color: #f5f5f5;
            font-weight: bold;
          }
          .signatures {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
            margin-top: 50px;
          }
          .signature-box {
            text-align: center;
          }
          .signature-box p {
            margin: 5px 0;
          }
          .signature-line {
            border-top: 1px solid #000;
            margin-top: 80px;
            padding-top: 5px;
          }
          .notes {
            margin-top: 20px;
            padding: 10px;
            background-color: #f9f9f9;
            border-left: 3px solid #666;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>SURAT JALAN</h1>
          <h2>No: ${mutation.mutation_no}</h2>
        </div>

        <div class="info-section">
          <div class="info-box">
            <h3>DARI CABANG</h3>
            <p><strong>Cabang:</strong> ${getBranchName(mutation.from_branch_id)}</p>
            <p><strong>Tanggal:</strong> ${new Date(mutation.created_at).toLocaleDateString('id-ID', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            })}</p>
          </div>
          <div class="info-box">
            <h3>KE CABANG</h3>
            <p><strong>Cabang:</strong> ${getBranchName(mutation.to_branch_id)}</p>
            <p><strong>Status:</strong> ${mutation.status}</p>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 50px;">No</th>
              <th>SKU</th>
              <th>Nama Barang</th>
              <th style="width: 100px; text-align: center;">Jumlah</th>
            </tr>
          </thead>
          <tbody>
            ${mutation.items && mutation.items.length > 0 ? mutation.items.map((item, idx) => `
              <tr>
                <td style="text-align: center;">${idx + 1}</td>
                <td>${item.item_sku}</td>
                <td>${item.item_name}</td>
                <td style="text-align: center;">${item.qty} pcs</td>
              </tr>
            `).join('') : '<tr><td colspan="4" style="text-align: center;">No items</td></tr>'}
          </tbody>
          <tfoot>
            <tr>
              <th colspan="3" style="text-align: right;">TOTAL ITEM:</th>
              <th style="text-align: center;">${mutation.items && mutation.items.length > 0 ? mutation.items.reduce((sum, item) => sum + item.qty, 0) : 0} pcs</th>
            </tr>
          </tfoot>
        </table>

        ${mutation.notes ? `
          <div class="notes">
            <p><strong>Catatan:</strong></p>
            <p>${mutation.notes}</p>
          </div>
        ` : ''}

        <div class="signatures">
          <div class="signature-box">
            <p><strong>Pengirim</strong></p>
            <div class="signature-line">
              <p>(_________________)</p>
            </div>
          </div>
          <div class="signature-box">
            <p><strong>Penerima</strong></p>
            <div class="signature-line">
              <p>(_________________)</p>
            </div>
          </div>
        </div>

        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };
  
  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">Mutasi Stok</h1>
            <button
              onClick={() => setShowCreate(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              + Buat Mutasi Baru
            </button>
          </div>
          
          {/* Filters */}
          <div className="mt-4 flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-2 border rounded"
            >
              <option value="ALL">Semua Status</option>
              <option value={MutationStatus.PENDING}>Pending</option>
              <option value={MutationStatus.CONFIRMED}>Confirmed</option>
              <option value={MutationStatus.CANCELLED}>Cancelled</option>
            </select>
            
            <input
              type="text"
              placeholder="Cari No. Mutasi, SKU, atau Nama Barang..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-3 py-2 border rounded"
            />
          </div>
        </div>
        
        {/* Mutation List */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredMutations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchQuery ? 'Tidak ada hasil pencarian' : 'Tidak ada mutasi'}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">No. Mutasi</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dari Cabang</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ke Cabang</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredMutations.map((mutation) => (
                  <tr key={mutation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      {mutation.mutation_no}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getBranchName(mutation.from_branch_id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getBranchName(mutation.to_branch_id)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        {mutation.items && mutation.items.length > 0 ? (
                          mutation.items.map((item, idx) => (
                            <div key={idx}>
                              {item.item_name} ({item.qty} pcs)
                            </div>
                          ))
                        ) : (
                          <span className="text-gray-400">No items</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(mutation.status)}`}>
                        {mutation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(mutation.created_at).toLocaleDateString('id-ID')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        {mutation.status === MutationStatus.PENDING && (
                          <button className="text-blue-600 hover:text-blue-800">
                            Konfirmasi
                          </button>
                        )}
                        <button
                          onClick={() => printDeliveryNote(mutation)}
                          className="text-green-600 hover:text-green-800 flex items-center gap-1"
                          title="Cetak Surat Jalan"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                          Print
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      
      {/* Create Mutation Modal */}
      {showCreate && (
        <CreateMutationModal
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false);
            loadMutations();
          }}
        />
      )}
    </div>
  );
};

export default CompleteMutationPage;
